import { huge, type HugeNumber } from "@/shared/numbers";
import { NHOV_BASE_HOV_THRESHOLD_VALUE } from "./nHovConstants";
import {
  normalizeNHovGrowthExponentBonus,
  resolveNHovBranchEffectiveLevel,
} from "./nHovBranchFormulaMath";
import { NHOV_FIRST_HG_AMPLIFY_MIN_BRANCH_LEVEL } from "./nHovLogic";
import type { NHovBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export const NHOV_HG_AMPLIFY_BASE_AT_LEVEL_ZERO = 1;
export const NHOV_HG_AMPLIFY_REWARD_REFERENCE_HOV =
  NHOV_BASE_HOV_THRESHOLD_VALUE;
export const NHOV_HG_AMPLIFY_VISIBLE_REFERENCE_MULTIPLIER = 100;
export const NHOV_HG_AMPLIFY_CURRENT_GROWTH_BASE = 2;
export const NHOV_HG_AMPLIFY_REWARD_AT_REFERENCE = 2;
export const NHOV_HG_AMPLIFY_EFFECTIVE_LEVEL_EXPONENT = 0.8;

function isFiniteNonNegative(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

// HugeNumber-preserving core so the multiplier grows past the JS number limit
// once the `outside-nhov` cap is lifted.
export function calculateNHovHgAmplifyCurrentHuge(
  level: number,
  exponentBonus = 0,
  balance?: NHovBalance,
): HugeNumber {
  if (!isFiniteNonNegative(level) || level <= 0) {
    return huge(NHOV_HG_AMPLIFY_BASE_AT_LEVEL_ZERO);
  }

  const bonus = normalizeNHovGrowthExponentBonus(exponentBonus);
  const branchBalance = balance?.branches.hgAmplify;
  return huge(
    branchBalance?.visibleReferenceMultiplier ??
      NHOV_HG_AMPLIFY_VISIBLE_REFERENCE_MULTIPLIER,
  )
    .add(resolveNHovHgAmplifyGrowthHuge(level, bonus, balance))
    .sub(
      resolveNHovHgAmplifyGrowthHuge(
        balance?.firstHgAmplifyMinBranchLevel ??
          NHOV_FIRST_HG_AMPLIFY_MIN_BRANCH_LEVEL,
        bonus,
        balance,
      ),
    );
}

function resolveNHovHgAmplifyGrowthHuge(
  level: number,
  exponentBonus: number,
  balance?: NHovBalance,
): HugeNumber {
  const branchBalance = balance?.branches.hgAmplify;
  const effectiveLevel = resolveNHovBranchEffectiveLevel({
    level,
    exponent:
      (branchBalance?.effectiveLevelExponent ??
        NHOV_HG_AMPLIFY_EFFECTIVE_LEVEL_EXPONENT) + exponentBonus,
  });

  return huge(branchBalance?.growthBase ?? NHOV_HG_AMPLIFY_CURRENT_GROWTH_BASE)
    .pow(effectiveLevel)
    .mul(NHOV_HG_AMPLIFY_BASE_AT_LEVEL_ZERO);
}

// `canExecuteNHovBranch` is the authoritative execution gate, so the reward
// gain itself does not enforce a minimum threshold here.
export function calculateNHovHgAmplifyRewardGain(
  currentHovLoopEarned: number,
): number {
  if (!isFiniteNonNegative(currentHovLoopEarned) || currentHovLoopEarned <= 0) {
    return 0;
  }
  const ratio = currentHovLoopEarned / NHOV_HG_AMPLIFY_REWARD_REFERENCE_HOV;
  const gain = NHOV_HG_AMPLIFY_REWARD_AT_REFERENCE * Math.sqrt(ratio);
  if (!Number.isFinite(gain) || gain <= 0) return 0;
  return gain;
}

export function calculateNHovHgAmplifyMultiplierHuge(
  level: number,
  exponentBonus = 0,
  balance?: NHovBalance,
): HugeNumber {
  return calculateNHovHgAmplifyCurrentHuge(level, exponentBonus, balance);
}
